import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import BrandLogo from "../components/BrandLogo";
import { toast, Toaster } from "sonner";
import api from "../lib/api";
import { Handshake, Eye, EyeOff, ShieldCheck } from "lucide-react";

const TERMS_VERSION = "2026-05-21";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refCode = (params.get("ref") || "").toUpperCase().trim();
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [partner, setPartner] = useState(null);

  useEffect(() => {
    if (!refCode) return;
    api.get(`/partners/lookup/${refCode}`)
      .then(({ data }) => setPartner(data))
      .catch(() => setPartner(null));
  }, [refCode]);

  const submit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      toast.error("Please accept the Wholesale Terms to continue.");
      return;
    }
    setLoading(true);
    try {
      await register({
        ...form,
        referral_code: refCode || undefined,
        accepted_terms: true,
        accepted_terms_version: TERMS_VERSION,
      });
      toast.success(partner ? `Welcome — referred by ${partner.name}` : "Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FAFAFA]">
      <Toaster position="top-right" richColors />
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-md p-8">
        <Link to="/" className="inline-block mb-6"><BrandLogo /></Link>
        <h1 className="font-display text-2xl font-black tracking-tighter mb-2">Create your account</h1>
        <p className="text-sm text-zinc-600 mb-6">
          Already have an account? <Link to="/login" className="brand-text font-medium">Sign in</Link>
        </p>
        {partner && (
          <div data-testid="partner-referral-banner" className="mb-5 bg-emerald-50 border border-emerald-300 rounded-sm p-3 inline-flex items-start gap-2 text-sm text-emerald-900">
            <Handshake size={16} className="mt-0.5 shrink-0" />
            <div>
              You were referred by <b>{partner.name}</b>{partner.company ? ` (${partner.company})` : ""}.
              <div className="text-[11px] text-emerald-700/80 mt-0.5">Code: <code className="font-mono-plex">{partner.referral_code}</code></div>
            </div>
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <Input data-testid="register-name-input" label="Full name" value={form.name}
            onChange={(v) => setForm({ ...form, name: v })} required />
          <Input data-testid="register-email-input" label="Email" type="email" value={form.email}
            onChange={(v) => setForm({ ...form, email: v })} required />
          <Input data-testid="register-company-input" label="Company (optional)" value={form.company}
            onChange={(v) => setForm({ ...form, company: v })} />
          <Input data-testid="register-password-input" label="Password" type="password" value={form.password}
            onChange={(v) => setForm({ ...form, password: v })} required minLength={6} />

          <div className="border border-zinc-200 rounded-sm bg-zinc-50 p-3 flex items-start gap-3" data-testid="register-wholesale-notice">
            <ShieldCheck size={18} className="brand-text mt-0.5 shrink-0" />
            <div className="text-[11px] text-zinc-700 leading-relaxed">
              <b>SellePack is a wholesale platform.</b> There are no monthly fees. Access is distributed exclusively through
              our approved partners and cannot be obtained individually. Charges are due on demand — no trade credit, no NET-30.
            </div>
          </div>

          <label className="flex items-start gap-2 text-xs text-zinc-700 cursor-pointer select-none">
            <input
              data-testid="register-accept-terms-checkbox"
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[var(--brand-accent)] shrink-0"
            />
            <span>
              I have read and agree to the{" "}
              <Link to="/legal/terms" target="_blank" className="brand-text hover:underline">Wholesale Terms of Service</Link>,{" "}
              <Link to="/legal/shipping-terms" target="_blank" className="brand-text hover:underline">Shipping Terms &amp; Policy</Link>,{" "}
              <Link to="/legal/regulatory-disclosure" target="_blank" className="brand-text hover:underline">Regulatory Disclosure</Link>, and{" "}
              <Link to="/legal/privacy" target="_blank" className="brand-text hover:underline">Privacy Policy</Link>.
            </span>
          </label>

          <button
            data-testid="register-submit-button"
            disabled={loading || !acceptedTerms}
            className="w-full brand-bg text-white py-2.5 rounded-sm font-medium hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Creating…" : "Create account"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, minLength, ...rest }) {
  const [reveal, setReveal] = useState(false);
  const isPassword = type === "password";
  const inputType = isPassword && reveal ? "text" : type;
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-zinc-500">{label}</label>
      <div className={`mt-1 relative`}>
        <input
          {...rest}
          type={inputType} required={required} minLength={minLength}
          value={value} onChange={(e) => onChange(e.target.value)}
          className={`w-full border border-zinc-300 rounded-sm px-3 py-2 ${isPassword ? "pr-10" : ""} focus:outline-none focus:border-[var(--brand-accent)]`}
        />
        {isPassword && (
          <button
            type="button"
            data-testid="toggle-password-visibility"
            onClick={() => setReveal((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-900"
            title={reveal ? "Hide password" : "Show password"}
          >
            {reveal ? <Eye size={16} /> : <EyeOff size={16} />}
          </button>
        )}
      </div>
    </div>
  );
}
